import { Buffer } from "buffer";
import {
  Connection,
  Keypair,
  PublicKey,
  SystemProgram,
  Transaction,
} from "@solana/web3.js";
import {
  MINT_SIZE,
  TOKEN_PROGRAM_ID,
  AuthorityType,
  getMinimumBalanceForRentExemptMint,
  getAssociatedTokenAddressSync,
  createInitializeMintInstruction,
  createAssociatedTokenAccountInstruction,
  createMintToInstruction,
  createSetAuthorityInstruction,
} from "@solana/spl-token";
import {
  createCreateMetadataAccountV3Instruction,
  PROGRAM_ID as METADATA_PROGRAM_ID,
} from "@metaplex-foundation/mpl-token-metadata";
import { BACKEND_URL } from "@/lib/api";

export const DECIMALS = 9;

// Connection requires an absolute URL — BACKEND_URL is "" on same-origin deploys,
// so fall back to window.location.origin (proxied to the backend RPC keeper).
const rpcUrl = (network) => `${BACKEND_URL || window.location.origin}/api/solana/rpc/${network}`;

export const getConnection = (network) => new Connection(rpcUrl(network), "confirmed");

export const metadataUriFor = (slug) =>
  `${BACKEND_URL || window.location.origin}/api/coins/${slug}/metadata.json`;

// Full launchpad-grade mint in ONE transaction:
// create mint (no freeze authority) → ATA → mint full supply to creator →
// attach Metaplex metadata (name/ticker/logo) → revoke mint authority (supply capped forever).
export function buildMintTransaction({ payer, mintKeypair, name, symbol, uri, supply, rentLamports, feeLamports = 0, treasury = null }) {
  const mint = mintKeypair.publicKey;
  const ata = getAssociatedTokenAddressSync(mint, payer);
  const [metadataPda] = PublicKey.findProgramAddressSync(
    [Buffer.from("metadata"), METADATA_PROGRAM_ID.toBuffer(), mint.toBuffer()],
    METADATA_PROGRAM_ID
  );
  const amount = BigInt(Math.round(Number(supply))) * BigInt(10 ** DECIMALS);

  const tx = new Transaction().add(
    SystemProgram.createAccount({
      fromPubkey: payer,
      newAccountPubkey: mint,
      space: MINT_SIZE,
      lamports: rentLamports,
      programId: TOKEN_PROGRAM_ID,
    }),
    createInitializeMintInstruction(mint, DECIMALS, payer, null),
    createAssociatedTokenAccountInstruction(payer, ata, payer, mint),
    createMintToInstruction(mint, ata, payer, amount),
    createCreateMetadataAccountV3Instruction(
      { metadata: metadataPda, mint, mintAuthority: payer, payer, updateAuthority: payer },
      {
        createMetadataAccountArgsV3: {
          data: {
            name: String(name).slice(0, 32),
            symbol: String(symbol).slice(0, 10),
            uri: String(uri).slice(0, 200),
            sellerFeeBasisPoints: 0,
            creators: null,
            collection: null,
            uses: null,
          },
          isMutable: false,
          collectionDetails: null,
        },
      }
    ),
    createSetAuthorityInstruction(mint, payer, AuthorityType.MintTokens, null)
  );
  if (feeLamports > 0 && treasury) {
    tx.add(SystemProgram.transfer({ fromPubkey: payer, toPubkey: new PublicKey(treasury), lamports: feeLamports }));
  }
  return tx;
}

export async function mintCoinOnChain({ network, walletAddress, coin, metadataUri, onStep, feeLamports = 0, treasury = null }) {
  const provider = window.solana;
  const connection = getConnection(network);
  const payer = new PublicKey(walletAddress);
  const mintKeypair = Keypair.generate();

  onStep?.("building");
  const rentLamports = await getMinimumBalanceForRentExemptMint(connection);
  const tx = buildMintTransaction({
    payer,
    mintKeypair,
    name: coin.name,
    symbol: coin.ticker,
    uri: metadataUri,
    supply: coin.supply,
    rentLamports,
    feeLamports,
    treasury,
  });
  const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash("confirmed");
  tx.recentBlockhash = blockhash;
  tx.feePayer = payer;
  tx.partialSign(mintKeypair);

  onStep?.("signing");
  const signed = await provider.signTransaction(tx);

  onStep?.("sending");
  const signature = await connection.sendRawTransaction(signed.serialize(), {
    skipPreflight: false,
    maxRetries: 3,
  });

  onStep?.("confirming");
  await connection.confirmTransaction({ signature, blockhash, lastValidBlockHeight }, "confirmed");

  return { signature, mintAddress: mintKeypair.publicKey.toBase58() };
}

export async function requestDevnetAirdrop(walletAddress) {
  const connection = getConnection("devnet");
  const sig = await connection.requestAirdrop(new PublicKey(walletAddress), 1_000_000_000);
  await connection.confirmTransaction(sig, "confirmed");
  return sig;
}
